/* eslint-disable max-statements */
import { useCallback, useEffect, useState } from 'react';
import { Text } from '@nimbus-ds/components';
import dayjs from 'dayjs';
import { generatePath } from 'react-router';
import { ErrorState } from '@tiendanube/components';
import { useDesktopMenuContext } from 'App/components/Menu/DesktopMenuProvider';
import { useToastProgress } from 'App/components/ToastProgressContext';
import useTopBar from 'App/components/Topbar/useTopBar';
import { useNavegate } from 'App/hooks';
import {
  HeaderContent,
  HeaderTop,
  IonPageStratus,
  Responsive,
} from 'commons/components';
import Stack from 'commons/components/Stack';
import { useAsyncFunc, useBoolean, useModal, useToast } from 'commons/hooks';
import { combineStatus } from 'commons/utils/combineStatus';
import goToAdmin from 'commons/utils/gotToAdmin';
import useGetStorePaymentDiscount from 'domains/Auth/hooks/useGetStorePaymentDiscount';
import { billingRoutes, useTranslationBilling } from 'domains/Billing';
import { CHECKOUT_ROUTES } from 'domains/Billing/Checkout/checkoutRoutes';
import {
  useAfterPaymentRedirection,
  useTrackBillingPayOrderCheckoutFinish,
} from 'domains/Billing/Checkout/hooks';
import { useHasMigratedToBillingEngine } from 'domains/Billing/hooks';
import {
  trackingBillingChangePlanClick,
  trackingBillingGoToPayPlanClick,
  trackingBillingPlansPageView,
} from 'domains/Billing/tracking';
import {
  ComparisonPlansCards,
  PlanCardSkeleton,
  ComparisonPlansCardsDesktopSkeleton,
  ComparisonTable,
  ComparisonTableMobileSkeleton,
  ComparisonTableDesktopSkeleton,
  ChangePlanModal,
  ChangePlanAlert,
  PlanDowngradeAlert,
} from './components';
import { SelectedPlan } from './components/ComparisonPlansCards/components/PlanCard/types';
import {
  useBillingPlans,
  usePlanDowngrade,
  useShouldScheduleDowngrade,
} from '../../hooks';

export type ChangeType = 'basic' | 'downgrade' | 'upgrade';

function BillingPlans(): JSX.Element {
  const t = useTranslationBilling();
  const {
    shouldScheduleDowngrade,
    status: scheduleDowngradeSetupStatus,
    retryLoad: retrySchedlueDowngradeLoad,
  } = useShouldScheduleDowngrade();
  const { addToast } = useToast();
  const { isShowMenu, showMenu } = useTopBar();
  const { isShowMenu: isShowAsideMenu, showMenu: showAsideMenu } =
    useDesktopMenuContext();
  const { goBack } = useNavegate();
  const { addToastProgress, closeToastProgress } = useToastProgress();
  const { status: hasMigratedStatus, fetchStoreSubscriptionIsEnabled } =
    useHasMigratedToBillingEngine();
  const { status: downgradeStatus, retryLoad: retryPlanDowngradeLoad } =
    usePlanDowngrade();
  const {
    status: billingPlansStatus,
    features,
    plansList,
    currentPlanId,
    currentPlan,
    currentPlanLevel,
    fetchBillingPlans,
    updateBillingPlan,
    openUpgradedPlanModal,
  } = useBillingPlans();
  const { isLoading, isError, isSuccess } = combineStatus(
    billingPlansStatus,
    hasMigratedStatus,
    downgradeStatus,
    scheduleDowngradeSetupStatus,
  );
  const retryLoad = useCallback(() => {
    fetchStoreSubscriptionIsEnabled();
    fetchBillingPlans();
    retryPlanDowngradeLoad();
    retrySchedlueDowngradeLoad();
  }, [
    fetchBillingPlans,
    fetchStoreSubscriptionIsEnabled,
    retryPlanDowngradeLoad,
    retrySchedlueDowngradeLoad,
  ]);
  const storePaymentDiscount = useGetStorePaymentDiscount();
  const { addAfterPayActionToPath } = useAfterPaymentRedirection();

  const { goTo } = useNavegate();

  useTrackBillingPayOrderCheckoutFinish();

  const [isOpenChangePlanModal, openChangePlanModal, closeChangePlanModal] =
    useModal();
  const [selectedPlan, setSelectedPlan] = useState<SelectedPlan>();
  const [changeType, setChangeType] = useState<ChangeType>();
  const [showAlert, setShowAlert] = useState(false);
  const [startPlanChangeFlow, initializePlanChangeFlow, resetPlanChangeFlow] =
    useBoolean(false);

  const [handleOnConfirmChangePlan, , error, clearError] = useAsyncFunc(
    useCallback(async () => {
      if (!selectedPlan) return;
      addToastProgress({
        label:
          changeType === 'upgrade'
            ? t('billingPlansPage.toast.loadingUpgrade')
            : t('billingPlansPage.toast.loadingDowngrade'),
      });
      const { needRedirect, paymentId, payOrderId } = await updateBillingPlan(
        selectedPlan.id,
      );

      if (needRedirect) {
        if (paymentId) {
          goToAdmin(`account/checkout/payment/${paymentId}`)();
          return;
        }
        payOrderId &&
          goTo(
            addAfterPayActionToPath(
              generatePath(CHECKOUT_ROUTES.choosePaymentMethodForPayOrder, {
                payOrderId,
              }),
              undefined,
            ),
          );
      }

      if (changeType === 'downgrade') {
        addToast({
          label: t('billingPlansPage.toast.successDowngrade'),
          appearance: 'success',
        });
      }

      if (changeType === 'upgrade') openUpgradedPlanModal();

      if (!shouldScheduleDowngrade || changeType === 'upgrade') {
        setShowAlert(true);
      }
      closeToastProgress();
      closeChangePlanModal();

      if (storePaymentDiscount && changeType === 'upgrade') {
        goTo(billingRoutes.plansAndSubscriptions);
      }
    }, [
      addAfterPayActionToPath,
      addToast,
      addToastProgress,
      changeType,
      closeChangePlanModal,
      closeToastProgress,
      goTo,
      openUpgradedPlanModal,
      selectedPlan,
      storePaymentDiscount,
      t,
      updateBillingPlan,
      shouldScheduleDowngrade,
    ]),
  );

  useEffect(() => {
    trackingBillingPlansPageView();
  }, []);

  useEffect(() => {
    if (error) {
      addToast({
        label:
          changeType === 'upgrade'
            ? t('billingPlansPage.toast.errorUpgrade')
            : t('billingPlansPage.toast.errorDowngrade'),
        appearance: 'danger',
      });
      closeChangePlanModal();
      clearError();
    }
  }, [error, changeType, clearError, addToast, closeChangePlanModal, t]);

  const newPlan = plansList.find((p) => p.id === selectedPlan?.id);

  const handleOnPlanSelected = (
    position: 'top' | 'bottom',
    plan: SelectedPlan,
  ): void => {
    const newPlan = plansList.find(({ id }) => id === plan.id);
    if (plan.id === currentPlanId) {
      trackingBillingGoToPayPlanClick(position, newPlan?.name);
      return goToAdmin('account/checkout/start/')();
    }

    setShowAlert(false);
    setSelectedPlan(plan);
    const changeType =
      plan.level === 0 && plan.isFree
        ? 'basic'
        : plan.level > currentPlanLevel
        ? 'upgrade'
        : 'downgrade';
    setChangeType(changeType);
    trackingBillingChangePlanClick({
      position,
      newPlanId: newPlan?.id,
      currentPlanId,
      changeType,
      scheduledDowngrade:
        (changeType === 'downgrade' || changeType === 'basic') &&
        shouldScheduleDowngrade,
    });
    initializePlanChangeFlow(); // This will trigger the effect below to open the modal or change plan immediately
  };

  const handleOnCancelChangePlan = (): void => {
    closeChangePlanModal();
    setSelectedPlan(undefined);
  };

  useEffect(() => {
    if (!startPlanChangeFlow) return;
    resetPlanChangeFlow();

    // If there's a discount, skip the modal and chanmge plan immediately
    if (storePaymentDiscount && changeType === 'upgrade') {
      handleOnConfirmChangePlan();
    } else {
      openChangePlanModal();
    }
  }, [
    startPlanChangeFlow,
    storePaymentDiscount,
    changeType,
    resetPlanChangeFlow,
    openChangePlanModal,
    handleOnConfirmChangePlan,
  ]);

  return (
    <IonPageStratus
      headerContent={<HeaderContent title={t('billingPlansPage.title')} />}
      headerTop={
        !isShowMenu &&
        !isShowAsideMenu &&
        !!storePaymentDiscount && (
          <HeaderTop
            navigation={{
              onClick() {
                goBack();
                showMenu();
                showAsideMenu();
              },
              children: t('billingPlansPage.back'),
            }}
          />
        )
      }
    >
      <ChangePlanAlert
        show={showAlert}
        changeType={changeType}
        newPlanName={newPlan?.name}
      />
      <PlanDowngradeAlert />

      {isLoading && (
        <Stack column align="stretch" spacing="loose">
          <Stack.Item>
            <Responsive
              mobile={<PlanCardSkeleton />}
              desktop={<ComparisonPlansCardsDesktopSkeleton />}
            />
          </Stack.Item>
          <Stack.Item>
            <Responsive
              mobile={<ComparisonTableMobileSkeleton />}
              desktop={<ComparisonTableDesktopSkeleton />}
            />
          </Stack.Item>
        </Stack>
      )}
      {isSuccess && (
        <Stack column align="stretch" spacing="loose">
          <Stack.Item>
            <ComparisonPlansCards
              onSelectPlan={(plan) => handleOnPlanSelected('top', plan)}
            />
          </Stack.Item>
          <Stack.Item>
            {features.length !== 0 && (
              <ComparisonTable
                onSelectPlan={(plan) => handleOnPlanSelected('bottom', plan)}
              />
            )}
          </Stack.Item>
        </Stack>
      )}
      {isError && (
        <ErrorState
          title={t('billingPlansPage.errorState.title')}
          action={{
            children: t('billingPlansPage.errorState.retry'),
            onClick: retryLoad,
          }}
        />
      )}
      {storePaymentDiscount && (
        <>
          <Text textAlign="center">
            {t('billingPlansPage.paymentDiscountLegal', {
              percentage: storePaymentDiscount.percentage,
              expirationDate: dayjs(storePaymentDiscount.expirationDate)
                .tz('UTC')
                .format('DD/MM'),
            })}
          </Text>
          <Text textAlign="center">
            {t('billingPlansPage.paymentDiscountLegal2')}
          </Text>
          <Text textAlign="center">
            {t('billingPlansPage.paymentDiscountLegal3')}
          </Text>
        </>
      )}

      <ChangePlanModal
        show={isOpenChangePlanModal}
        changeType={changeType}
        onConfirm={handleOnConfirmChangePlan}
        onCancel={handleOnCancelChangePlan}
        newPlan={newPlan}
        currentPlan={currentPlan}
      />
    </IonPageStratus>
  );
}

export default BillingPlans;
